import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/shared/Logo";
import { Loader2 } from "lucide-react";

export default function CustomerRegister() {
    const { persistSession } = useAuth();
    const nav = useNavigate();
    const [districts, setDistricts] = useState([]);
    const [form, setForm] = useState({ mobile_number: "", password: "", full_name: "", district_slug: "", email: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
        if (!/^[6-9]\d{9}$/.test(form.mobile_number)) return toast.error("Enter a 10-digit Indian mobile number");
        if (!form.district_slug) return toast.error("Pick your district");
        setSubmitting(true);
        try {
            const body = { ...form };
            if (!body.email) delete body.email;
            const r = await api.post("/auth/customer/register", body);
            persistSession(r.data);
            toast.success("Welcome to OfferNest!");
            nav(`/posts?district_slug=${form.district_slug}`);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 px-4 py-10 text-white" data-testid="customer-register">
            <div className="mx-auto flex max-w-md flex-col items-center gap-6">
                <Link to="/"><Logo size={128} onDark /></Link>
                <div className="w-full rounded-3xl bg-white p-6 text-neutral-900 shadow-2xl">
                    <div className="mb-4">
                        <div className="text-sm font-medium text-orange-600">Customer signup</div>
                        <h1 className="text-2xl font-extrabold">Create your OfferNest account</h1>
                        <p className="mt-1 text-xs text-neutral-500">Save favorites, claim coupons, and see what's trending near you.</p>
                    </div>
                    <form className="grid gap-3" onSubmit={submit}>
                        <div>
                            <Label className="text-xs">Full name</Label>
                            <Input value={form.full_name} onChange={set("full_name")} required maxLength={120} data-testid="reg-fullname" />
                        </div>
                        <div>
                            <Label className="text-xs">Mobile number (10 digits)</Label>
                            <div className="flex">
                                <span className="inline-flex items-center rounded-l-md border border-r-0 border-neutral-300 bg-neutral-100 px-2 text-xs text-neutral-500">+91</span>
                                <Input value={form.mobile_number} onChange={set("mobile_number")} inputMode="numeric" maxLength={10} className="rounded-l-none" required data-testid="reg-mobile" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Password (min 8 characters)</Label>
                            <Input type="password" value={form.password} onChange={set("password")} minLength={8} required data-testid="reg-password" />
                        </div>
                        <div>
                            <Label className="text-xs">District</Label>
                            <Select value={form.district_slug || undefined} onValueChange={set("district_slug")}>
                                <SelectTrigger data-testid="reg-district"><SelectValue placeholder="Pick your district" /></SelectTrigger>
                                <SelectContent>
                                    {districts.map((d) => (<SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Email (optional)</Label>
                            <Input type="email" value={form.email} onChange={set("email")} data-testid="reg-email" />
                        </div>
                        <Button type="submit" disabled={submitting} className="mt-2 h-11 nest-gradient text-base font-semibold text-white" data-testid="reg-submit">
                            {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                            Create Account
                        </Button>
                    </form>
                    <p className="mt-4 text-center text-xs text-neutral-500">
                        Already have an account? <Link to="/customer/login" className="font-semibold text-orange-600 hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
